import React, { useEffect, useState } from "react";
import { collection, getDocs, updateDoc, doc, deleteDoc, addDoc, onSnapshot, getDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from "../firebase";

function CourseAdmin() {
  const [courses, setCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingCourse, setEditingCourse] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [resumeData, setResumeData] = useState(null);
  const [newResumeFileName, setNewResumeFileName] = useState("");
  const [newResumeFilePath, setNewResumeFilePath] = useState("");

  // Real-time listener for courses
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "courses"), (snapshot) => {
      const coursesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCourses(coursesData);
      setIsLoading(false);
    }, (error) => {
      console.error("Error listening to courses:", error);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Fetch resume data
  useEffect(() => {
    const fetchResumeData = async () => {
      try {
        const homepageRef = doc(db, "homepage", "main");
        const homepageSnap = await getDoc(homepageRef);
        if (homepageSnap.exists()) {
          setResumeData(homepageSnap.data().resume);
        }
    } catch (error) {
        console.error("Error fetching resume data:", error);
      }
    };

    fetchResumeData();
  }, []);

  // Additional scroll reset for this component
  useEffect(() => {
    // Force scroll to top when this component mounts
    window.scrollTo(0, 0);
    
    // Also reset on document elements
    if (document.documentElement) {
      document.documentElement.scrollTop = 0;
    }
    if (document.body) {
      document.body.scrollTop = 0;
    }
  }, []);

  // Update resume function
  const updateResume = async () => {
    if (!newResumeFileName || !newResumeFilePath) {
      alert("Please fill in both file name and file path");
      return;
    }

    try {
      const homepageRef = doc(db, "homepage", "main");
      await updateDoc(homepageRef, {
        resume: {
          fileName: newResumeFileName,
          filePath: newResumeFilePath,
          lastUpdated: new Date().toISOString(),
          description: "Professional resume for Mohammed Kamal Ali"
        }
      });
      
      // Update local state
      setResumeData({
        fileName: newResumeFileName,
        filePath: newResumeFilePath,
        lastUpdated: new Date().toISOString(),
        description: "Professional resume for Mohammed Kamal Ali"
      });

      // Clear input fields
      setNewResumeFileName("");
      setNewResumeFilePath("");

      alert("Resume updated successfully!");
    } catch (error) {
      console.error("Error updating resume:", error);
      alert("Error updating resume. Please try again.");
    }
  };

  // Simple single image upload
  const uploadSingleImage = async (file) => {
    try {
      console.log('=== Starting single image upload ===');
      console.log('File:', file.name, 'Size:', file.size, 'Type:', file.type);
      
      // Create a unique filename
      const timestamp = Date.now();
      const fileName = `${timestamp}-${file.name}`;
      const storagePath = `project-screenshots/${fileName}`;
      
      console.log('Storage path:', storagePath);
      
      // Create storage reference
      const storageRef = ref(storage, storagePath);
      console.log('Storage reference created');
      
      // Upload the file
      console.log('Uploading file...');
      const uploadResult = await uploadBytes(storageRef, file);
      console.log('Upload completed:', uploadResult);
      
      // Get download URL
      console.log('Getting download URL...');
      const downloadURL = await getDownloadURL(uploadResult.ref);
      console.log('Download URL:', downloadURL);
      
      return downloadURL;
      } catch (error) {
      console.error('=== Upload Error ===');
      console.error('Error details:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      throw new Error(`Upload failed: ${error.message}`);
    }
  };

  // Upload multiple images one by one
  const uploadMultipleImages = async (files) => {
    console.log('=== Starting multiple image upload ===');
    console.log('Number of files:', files.length);
    
    setUploading(true);
      setUploadProgress(0);
      
    const uploadedUrls = [];
    
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        console.log(`\n--- Uploading file ${i + 1}/${files.length} ---`);
        console.log('File:', file.name);
        
        try {
          const url = await uploadSingleImage(file);
          uploadedUrls.push(url);
          console.log(`File ${i + 1} uploaded successfully:`, url);
          
          // Update progress
          const progress = ((i + 1) / files.length) * 100;
          setUploadProgress(progress);
          console.log(`Progress: ${progress.toFixed(1)}%`);
          
          // Wait a bit between uploads
          if (i < files.length - 1) {
            console.log('Waiting 2 seconds before next upload...');
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        } catch (fileError) {
          console.error(`Failed to upload file ${i + 1}:`, fileError);
          throw new Error(`Failed to upload ${file.name}: ${fileError.message}`);
        }
      }
      
      console.log('=== All files uploaded successfully ===');
      console.log('Uploaded URLs:', uploadedUrls);
      
      return uploadedUrls;
      } catch (error) {
      console.error('=== Multiple upload failed ===');
      console.error('Error:', error);
        throw error;
      } finally {
      setUploading(false);
        setUploadProgress(0);
      }
    };

  const updateCourse = async (courseId, updatedData) => {
    try {
      console.log('=== Updating Firestore document ===');
      console.log('Course ID:', courseId);
      console.log('Updated data:', updatedData);
      
      const courseRef = doc(db, "courses", courseId);
      await updateDoc(courseRef, updatedData);
      
      console.log('Firestore update completed successfully');
      alert("Course updated successfully!");
    } catch (error) {
      console.error("=== Firestore update error ===");
      console.error("Error:", error);
      throw new Error(`Firestore update failed: ${error.message}`);
    }
  };

  const deleteCourse = async (courseId) => {
    if (window.confirm("Are you sure you want to delete this course?")) {
      try {
        await deleteDoc(doc(db, "courses", courseId));
        alert("Course deleted successfully!");
      } catch (error) {
        console.error("Error deleting course:", error);
        alert("Failed to delete course");
      }
    }
  };

  const addNewCourse = async (courseData) => {
    try {
      await addDoc(collection(db, "courses"), courseData);
      setShowAddModal(false);
      alert("Course added successfully!");
    } catch (error) {
      console.error("Error adding course:", error);
      alert("Failed to add course");
    }
  };

  // Enhanced Edit Course Modal
  const EditCourseModal = ({ course, onClose }) => {
    const [formData, setFormData] = useState({
      name: course.name || "",
      code: course.code || "",
      summary: course.summary || "",
      category: course.category || "",
      type: course.type || "",
      semester: course.semester || "",
      imageUrl: course.imageUrl || "",
      hasProject: course.hasProject || false,
      project: course.project || {
        title: "",
        description: "",
        github: "",
        technologies: [],
        screenshots: []
      }
    });

    const [selectedCourseImage, setSelectedCourseImage] = useState(null);
    const [selectedScreenshots, setSelectedScreenshots] = useState([]);
    const [newTechnologies, setNewTechnologies] = useState("");

    const handleInputChange = (field, value) => {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    };

    const handleProjectChange = (field, value) => {
      setFormData(prev => ({
        ...prev,
        project: {
          ...prev.project,
          [field]: value
        }
      }));
    };

    const handleCourseImageChange = (e) => {
      const file = e.target.files[0];
      if (file) {
        setSelectedCourseImage(file);
        setFormData(prev => ({
          ...prev,
          imageUrl: URL.createObjectURL(file)
        }));
      }
    };

    const handleScreenshotsChange = (e) => {
      const files = Array.from(e.target.files);
      console.log('Files selected:', files.length);
      files.forEach((file, index) => {
        console.log(`File ${index + 1}:`, file.name, file.size, file.type);
      });
      
      if (files.length > 5) {
        alert("You can only upload up to 5 screenshots");
        return;
      }
      setSelectedScreenshots(files);
    };

    const addTechnology = () => {
      if (newTechnologies.trim()) {
        const techs = newTechnologies.split(',').map(tech => tech.trim()).filter(tech => tech);
        setFormData(prev => ({
          ...prev,
          project: {
            ...prev.project,
            technologies: [...(prev.project.technologies || []), ...techs]
          }
        }));
        setNewTechnologies("");
      }
    };

    const removeTechnology = (index) => {
      setFormData(prev => ({
        ...prev,
        project: {
          ...prev.project,
          technologies: prev.project.technologies.filter((_, i) => i !== index)
        }
      }));
    };

    const removeScreenshot = (index) => {
      setFormData(prev => ({
        ...prev,
        project: {
          ...prev.project,
          screenshots: prev.project.screenshots.filter((_, i) => i !== index)
        }
      }));
    };

    const handleSubmit = async (e) => {
      e.preventDefault();
      console.log('=== Starting course update process ===');
      setUploading(true);

      try {
        let finalImageUrl = formData.imageUrl;
        let finalScreenshots = [...formData.project.screenshots];

        console.log('Current screenshots count:', finalScreenshots.length);
        console.log('Selected new screenshots count:', selectedScreenshots.length);

        // Upload course image if selected
        if (selectedCourseImage) {
          console.log('Uploading course image:', selectedCourseImage.name);
          finalImageUrl = await uploadSingleImage(selectedCourseImage);
          console.log('Course image uploaded:', finalImageUrl);
        }
          
          // Upload new screenshots if selected
        if (selectedScreenshots && selectedScreenshots.length > 0) {
          console.log('Starting screenshot uploads...');
          console.log('Files to upload:', selectedScreenshots.map(f => f.name));
          
          const screenshotUrls = await uploadMultipleImages(selectedScreenshots);
          console.log('Screenshot URLs received:', screenshotUrls);
          
          if (screenshotUrls && screenshotUrls.length > 0) {
            const newScreenshots = screenshotUrls.map((url, index) => ({
              src: url,
              alt: `Screenshot ${finalScreenshots.length + index + 1}`
            }));
            finalScreenshots = [...finalScreenshots, ...newScreenshots];
            console.log('Final screenshots array:', finalScreenshots);
          } else {
            console.warn('No screenshot URLs received from upload');
          }
        } else {
          console.log('No new screenshots selected for upload');
        }

        const updatedData = {
          ...formData,
          imageUrl: finalImageUrl,
          project: formData.hasProject ? {
            ...formData.project,
            screenshots: finalScreenshots
          } : null
        };

        console.log('Final data to update:', updatedData);
        await updateCourse(course.id, updatedData);
        console.log('Course updated successfully');
        onClose();
      } catch (error) {
        console.error("=== Course update failed ===");
        console.error("Error:", error);
        alert(`Failed to update course: ${error.message}`);
      } finally {
        setUploading(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">Edit Course: {course.name}</h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Course Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Course Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Course Code</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => handleInputChange('code', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
            </div>
            
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Select Category</option>
                  <option value="Networking & Security">Networking & Security</option>
                  <option value="Software Engineering">Software Engineering</option>
                  <option value="Others">Others</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                <select
                  value={formData.type}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Select Type</option>
                  <option value="Core">Core</option>
                  <option value="Elective">Elective</option>
                  <option value="Lab">Lab</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Semester</label>
                <input
                  type="text"
                  value={formData.semester}
                  onChange={(e) => handleInputChange('semester', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., 1st, 2nd, 3rd"
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Has Project</label>
                <div className="flex items-center space-x-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.hasProject}
                      onChange={(e) => handleInputChange('hasProject', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">This course has a project</span>
                  </label>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Course Summary</label>
              <textarea
                value={formData.summary}
                onChange={(e) => handleInputChange('summary', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            {/* Course Image */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Course Image</label>
              <div className="flex items-center space-x-4">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleCourseImageChange}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {formData.imageUrl && (
                  <img
                    src={formData.imageUrl}
                    alt="Course preview"
                    className="w-20 h-20 object-cover rounded-lg border"
                  />
                )}
              </div>
            </div>

            {/* Project Information */}
            {formData.hasProject && (
              <div className="border-t pt-6 space-y-6">
                <h3 className="text-lg font-semibold text-gray-900">Project Information</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Project Title</label>
                  <input
                    type="text"
                      value={formData.project.title}
                      onChange={(e) => handleProjectChange('title', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">GitHub URL</label>
                  <input
                    type="url"
                      value={formData.project.github}
                      onChange={(e) => handleProjectChange('github', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="https://github.com/username/repo"
                  />
                </div>
              </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Project Description</label>
                <textarea
                    value={formData.project.description}
                    onChange={(e) => handleProjectChange('description', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

                {/* Technologies */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Technologies</label>
                  <div className="flex items-center space-x-2 mb-3">
                <input
                  type="text"
                      value={newTechnologies}
                      onChange={(e) => setNewTechnologies(e.target.value)}
                      placeholder="Add technologies (comma-separated)"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={addTechnology}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.project.technologies.map((tech, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                      >
                        {tech}
                        <button
                          type="button"
                          onClick={() => removeTechnology(index)}
                          className="ml-2 text-blue-600 hover:text-blue-800"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
              </div>

                {/* Screenshots */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Project Screenshots (Upload up to 5 new images)
                </label>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleScreenshotsChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  
                  {/* Current Screenshots */}
                  {formData.project.screenshots.length > 0 && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Current Screenshots ({formData.project.screenshots.length})</h4>
                    <div className="grid grid-cols-5 gap-2">
                        {formData.project.screenshots.map((screenshot, index) => (
                        <div key={index} className="relative group">
                          <img
                              src={screenshot.src}
                              alt={screenshot.alt}
                              className="w-full h-20 object-cover rounded-lg border"
                          />
                          <button
                            type="button"
                            onClick={() => removeScreenshot(index)}
                              className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                    </div>
                  </div>
                )}
                
            {/* Submit Button */}
            <div className="flex justify-end space-x-3 pt-6 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={uploading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {uploading ? 'Updating...' : 'Update Course'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // Add New Course Modal
  const AddCourseModal = ({ onClose }) => {
    const [formData, setFormData] = useState({
      name: "",
      code: "",
      summary: "",
      category: "",
      type: "",
      semester: "",
      imageUrl: "",
      hasProject: false,
      project: {
      title: "",
      description: "",
      github: "",
        technologies: [],
        screenshots: []
      }
    });

    const handleSubmit = async (e) => {
      e.preventDefault();
      await addNewCourse(formData);
    };

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">Add New Course</h2>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            {/* Similar form structure as EditCourseModal */}
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Course Name</label>
                <input
                  type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Course Code</label>
                <input
                  type="text"
                    value={formData.code}
                    onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
            </div>
            
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Course Summary</label>
              <textarea
                  value={formData.summary}
                  onChange={(e) => setFormData(prev => ({ ...prev, summary: e.target.value }))}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

              <div className="flex justify-end space-x-3">
                          <button
                            type="button"
                  onClick={onClose}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                          >
                  Cancel
                          </button>
              <button
                type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Add Course
              </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading courses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
              <h1 className="text-3xl font-bold text-gray-900">Course Management</h1>
              <p className="text-gray-600 mt-2">Manage all courses and their projects in real-time</p>
          </div>
          <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700"
          >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add New Course
          </button>
        </div>
      </div>

        {/* Courses Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map(course => (
            <div key={course.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Course Image */}
              <div className="aspect-video bg-gray-100">
                {course.imageUrl ? (
                  <img
                    src={course.imageUrl}
                    alt={course.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
        </div>
        
              {/* Course Info */}
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{course.name}</h3>
                    <p className="text-sm text-gray-600">{course.code}</p>
                  </div>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    course.hasProject 
                        ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                    }`}>
                    {course.hasProject ? 'Has Project' : 'No Project'}
                    </span>
                  </div>
                  
                <div className="space-y-2 mb-4">
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Category:</span> {course.category}
                  </p>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Type:</span> {course.type}
                  </p>
                  {course.semester && (
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Semester:</span> {course.semester}
                    </p>
                  )}
                        </div>

                {course.project && (
                  <div className="mb-4 p-3 bg-green-50 rounded-lg">
                    <h4 className="text-sm font-medium text-green-800 mb-1">Project: {course.project.title}</h4>
                    <p className="text-xs text-green-700">{course.project.screenshots?.length || 0} screenshots</p>
                    </div>
                  )}
                
                <div className="flex space-x-2">
                  <button
                    onClick={() => {
                      setEditingCourse(course);
                      setShowEditModal(true);
                    }}
                    className="flex-1 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => deleteCourse(course.id)}
                    className="px-3 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
      </div>

        {/* Modals */}
        {showEditModal && editingCourse && (
          <EditCourseModal
          course={editingCourse}
            onClose={() => {
              setShowEditModal(false);
            setEditingCourse(null);
          }}
        />
      )}

        {showAddModal && (
          <AddCourseModal
            onClose={() => setShowAddModal(false)}
          />
        )}

        {/* Upload Progress */}
        {uploading && (
          <div className="fixed bottom-4 right-4 bg-white rounded-lg shadow-lg p-4 border z-50">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              <div>
                <p className="text-sm font-medium text-gray-900">Uploading...</p>
                <p className="text-xs text-gray-600">{Math.round(uploadProgress)}% complete</p>
                <div className="w-32 bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Resume Management Section */}
        <div className="mt-12 p-6 bg-white rounded-lg shadow-lg border">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Resume Management</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800">Current Resume</h3>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">File Name:</span> {resumeData?.fileName || "Not set"}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">File Path:</span> {resumeData?.filePath || "Not set"}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Last Updated:</span> {resumeData?.lastUpdated ? new Date(resumeData.lastUpdated).toLocaleDateString() : "Not set"}
                </p>
              </div>
            </div>
            
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800">Update Resume</h3>
              <div className="space-y-3">
                <input
                  type="text"
                  placeholder="New File Name (e.g., Mohammed_Kamal_Ali_Resume_v2.pdf)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={newResumeFileName || ""}
                  onChange={(e) => setNewResumeFileName(e.target.value)}
                />
                <input
                  type="text"
                  placeholder="New File Path (e.g., /Resume/new-resume.pdf)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={newResumeFilePath || ""}
                  onChange={(e) => setNewResumeFilePath(e.target.value)}
                />
                <button
                  onClick={updateResume}
                  className="w-full px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Update Resume
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Homepage Content Management Section */}
        <div className="mt-12 p-6 bg-white rounded-lg shadow-lg border">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Homepage Content Management</h2>
          <div className="space-y-6">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="text-lg font-semibold text-blue-800 mb-2">Current Status</h3>
              <p className="text-sm text-blue-700">
                ✅ All homepage content is now dynamically loaded from Firebase! 
                <br />
                📝 You can edit any content directly in your Firestore database under the "homepage/main" document.
                <br />
                🔄 Changes will appear immediately on your portfolio without needing to restart the app.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800">Available Sections</h3>
                <div className="space-y-2 text-sm text-gray-600">
                  <p>• <strong>Hero:</strong> Title, subtitle, description, buttons</p>
                  <p>• <strong>About:</strong> Personal introduction content</p>
                  <p>• <strong>Skills:</strong> Categories, items, competencies</p>
                  <p>• <strong>Languages:</strong> Language proficiency levels</p>
                  <p>• <strong>Certifications:</strong> Achievements and details</p>
                  <p>• <strong>Experience:</strong> Work and leadership highlights</p>
                  <p>• <strong>Volunteering:</strong> Community involvement</p>
                  <p>• <strong>Projects:</strong> Portfolio projects and descriptions</p>
                  <p>• <strong>Personal Note:</strong> Quote and philosophy</p>
                  <p>• <strong>Footer:</strong> Contact links and description</p>
                </div>
              </div>
              
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800">How to Edit</h3>
                <div className="space-y-2 text-sm text-gray-600">
                  <p>1. Go to Firebase Console → Firestore</p>
                  <p>2. Navigate to "homepage" collection</p>
                  <p>3. Edit the "main" document</p>
                  <p>4. Save changes</p>
                  <p>5. Refresh your portfolio page</p>
                  <p>6. Changes appear instantly! 🚀</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CourseAdmin;
